import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import NavBar, { NavType, pathToNavType } from './NavBar/NavBar';
import Header from './Header/Header';
import { AuthContext } from '../utils/contextapi/AuthProvider';
import { OAUTH } from '../utils/Config';
import axios from '../utils/api/axios';
// import { DialogEnum, showDialog } from '../utils/dialog/Dialog';
import { InnocodeFooter, useDialog } from '@intsoft/innocode-core';
import moment from 'moment'; // moment import
import { refreshAccessToken, } from '../utils/token/token'; // token 유틸 import
import { setCookie, getCookie, deleteCookie } from '../utils/cookies/cookies.js';
import FooterInfo from './NavBar/component/FooterInfo';
import styles from './Layout.module.css';

const Layout = ({ setMovePage }) => {
  const { showDialog } = useDialog();
  const {
    isAuthenticated, saveAuthenticated, saveToken, saveTokenExpiresIn,
    saveRole, saveUser, user, tokenExpiresIn, doLogout, saveRefreshToken
  } = useContext(AuthContext);

  const location = useLocation();
  const [searchParams] = useSearchParams(); // ✨ useSearchParams 훅 사용

  // 'view' 쿼리 파라미터 값을 읽어옵니다.
  const viewMode = searchParams.get('view');
  const enableGobackBar = searchParams.get('enableGobackBar');

  const [pageType, setPageType] = useState(null);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const movePage = useNavigate();

  // --- Header에서 옮겨온 상태와 로직 ---
  const [tokenExpiredTime, setTokenExpiredTime] = useState({ minute: 0, second: 0 });
  const [sessionIntervalStatus, setSessionIntervalStatus] = useState(true);
  const dialogShownRef = useRef(false);

  const sessionLogout = useCallback(() => {

    // showDialog(DialogEnum.CONFIRM, "로그아웃", "정말 로그아웃하시겠습니까?", () => {
    //   setSessionIntervalStatus(false);
    //   doLogout();
    //   showDialog(DialogEnum.SUCCESS, "로그아웃", "로그아웃 되었습니다.");
    // });

    showDialog({
      iconType: "warning",
      type: 'delete',
      title: "로그아웃",
      message: "로그아웃 하시겠습니까?",
      confirmText: "예",
      cancelText: "아니요",
      onConfirm: () => {
        setSessionIntervalStatus(false);
        doLogout();
        },
    });
  }, [doLogout, setSessionIntervalStatus, showDialog]);

  const handleRefresh = useCallback(async () => {
    dialogShownRef.current = false;
    const success = await refreshAccessToken(saveToken, saveTokenExpiresIn, saveAuthenticated, saveRole, saveUser, movePage);
    // if (success) {
    //     showDialog(DialogEnum.SUCCESS, "세션 연장", "로그인이 연장되었습니다.");
    // }
  }, [saveToken, saveTokenExpiresIn, saveAuthenticated, saveRole, saveUser, movePage]);

  useEffect(() => {
    const shouldRunTimer = isAuthenticated && tokenExpiresIn;

    if (!shouldRunTimer) {
      // 로그인 상태가 아니거나 만료 시간이 없으면 타이머 표시 안 함
      setTokenExpiredTime(null);
      return;
    }

    const intervalId = setInterval(() => {
      // parseInt로 숫자로 변환하는 것을 보장
      const expiryTimestamp = parseInt(tokenExpiresIn, 10);

      // moment()는 현재 시간을 가져옴
      const diffTime = moment.duration(moment(expiryTimestamp).diff(moment()));
      const remainingSeconds = diffTime.asSeconds();

      if (remainingSeconds > 0) {
        setTokenExpiredTime({
          minute: Math.floor(remainingSeconds / 60),
          second: Math.floor(remainingSeconds % 60)
        });

        if (remainingSeconds <= 60 && !dialogShownRef.current) {
          // dialogShownRef.current = true;
          // handleRefresh();
          // showDialog(DialogEnum.CONFIRM, "세션 만료 임박", "로그인을 연장 하시겠습니까?", handleRefresh, () => {});
        }
      } else if (remainingSeconds <= 0) {
        // handleRefresh();
        setTokenExpiredTime({ minute: 0, second: 0 });
        clearInterval(intervalId);
        // sessionLogout();
      }
    }, 1000);
    return () => clearInterval(intervalId);

  }, [sessionIntervalStatus, tokenExpiresIn, sessionLogout, handleRefresh]);

  useEffect(() => {
    // 현재 경로에 맞는 NavType을 찾습니다.
    const currentPath = location.pathname;
    const matchedPath = Object.keys(pathToNavType).find(path => path === currentPath);

    const newPageType = matchedPath ? pathToNavType[matchedPath] : null;
    setPageType(newPageType);

    if (setMovePage && newPageType) {
      setMovePage(newPageType);
    }
  }, [location, setMovePage]);

  // 토큰 유효성 검사 useEffect
  // 주기적인 백그라운드 토큰 검증 로직 추가
  useEffect(() => {
    // 이 함수는 백그라운드에서 조용히 토큰 유효성을 확인합니다.
    const verifyTokenPeriodically = async () => {
      // 로그인 상태일 때만 검증을 시도합니다.
      if (isAuthenticated) {
        try {
          await axios.get(`${OAUTH}/user-token-info`);
          // 성공하면 아무것도 할 필요가 없습니다. 계속 로그인 상태를 유지합니다.
        } catch (error) {
          // 🚨 실패 시(예: 401 Unauthorized) 즉시 로그아웃 처리
          console.error("Background token validation failed:", error);

          const tokenData = await refreshAccessToken();
          if (tokenData) {

            const { access_token, expires_in, refresh_token, exp_timestamp, refresh_token_expires_in, user, roles } = tokenData;

            let finalExpiresIn = exp_timestamp ? exp_timestamp : Date.now() + (expires_in * 1000);
            const expiryDate = new Date(finalExpiresIn);

            saveToken(access_token, expiryDate);
            saveRefreshToken(refresh_token, refresh_token_expires_in);
            saveUser(user, expiryDate);
            saveRole(roles, expiryDate);
            // showDialog(DialogEnum.SUCCESS, "세션 연장", "로그인이 연장되었습니다.");
            return;
          } else {
            showDialog({
              iconType: "warning",
              type: 'delete',
              title: "세션 만료",
              message: "세션이 만료되었습니다. 다시 로그인 해주세요.",
              confirmText: "예",
              cancelText: "아니요",
              onConfirm: () => { 
                
                },
            });
            // showDialog(DialogEnum.DEFAULT, "세션 만료", "세션이 만료되었습니다. 다시 로그인 해주세요.");
            doLogout();
          }
        }
      }
    };

    // 페이지에 처음 진입했을 때 한 번 실행
    verifyTokenPeriodically();

    // 5분(300000ms)마다 주기적으로 실행
    const intervalId = setInterval(verifyTokenPeriodically, 300000);

    // 컴포넌트가 언마운트될 때 interval을 정리합니다.
    return () => clearInterval(intervalId);

  }, [isAuthenticated, doLogout]); // isAuthenticated 상태가 바뀔 때마다 이 로직을 재설정





  // AuthProvider가 제공하는 isAuthenticated 값을 직접 사용합니다.
  // AuthProvider가 초기 로딩을 처리해주므로, 여기서는 로딩 상태를 신경 쓸 필요가 없습니다.
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <div className="innocode-breakpoint-layout-bg">
      <div className="innocode-breakpoint-content-admin-xl" >
        <div className="layout-container">
          <div className="adminWrap">

            <NavBar
              type={pageType}
              isMobileMenuOpen={isMobileMenuOpen}
              setIsMobileMenuOpen={setIsMobileMenuOpen}
              user={user}
              tokenExpiredTime={tokenExpiredTime}
              sessionLogout={sessionLogout}
              viewMode={viewMode}
            />
            <section className={styles.rightCont}>
              <Header
                  setIsMobileMenuOpen={setIsMobileMenuOpen}
                  user={user}
                  tokenExpiredTime={tokenExpiredTime}
                  sessionLogout={sessionLogout}
                  viewMode={viewMode}
                  enableGobackBar={enableGobackBar}
                />
              <div className={styles.rightCont__cont}>
                <Outlet context={{ setMovePage }} />
              </div>
              <InnocodeFooter />
            </section>
          </div>
        </div>
      </div>
    </div>


    // <div className="innocode-breakpoint-layout-bg">
    //     <div className="innocode-breakpoint-content-admin" >
    //         <div className="layout-container">
    //             {/* Mobile Header - Mobile Only */}
    //             {enableNavbar && (
    //                 <header className="mobile-header">
    //                     <InnocodeButton onClick={toggleMobileMenu} designType={'assistive'} theme={'opacity'} iconOnly={true} icon={
    //                         <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    //                             <path d="M3 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    //                             <path d="M3 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    //                             <path d="M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    //                         </svg>
    //                     }></InnocodeButton>
    //                 </header>
    //             )}
    //             {enableGobackBar && (
    //                 <header className="mobile-header">
    //                     <InnocodeButton onClick={() => { window.history.back() }} designType={'assistive'} theme={'opacity'} iconOnly={true} icon={
    //                         <svg className="w-6 h-6 text-gray-800" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
    //                             <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m15 19-7-7 7-7" />
    //                         </svg>
    //                     }></InnocodeButton>
    //                 </header>
    //             )}

    //             {/* Mobile Menu Overlay */}
    //             {(enableNavbar && isMobileMenuOpen) && (
    //                 <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>
    //             )}
    //             <div className={"admin-layout-wrapper " + ((enableNavbar || enableGobackBar) ? 'with-navbar' : '')} >
    //                 {enableNavbar && (
    //                     <div className={`navbar-container ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
    //                         <AdminNavbar currentPageUrl={currentPageUrl} menuData={menuData} />
    //                     </div>
    //                 )}
    //                 <div className="content-wrapper">
    //                     {children}
    //                 </div>
    //             </div>
    //         </div>
    //     </div>
    // </div>


  );
};

export default Layout;